import firebase from 'firebase/app';
import axios from 'axios';
import { t } from '@/plugin/vue-lang';
import * as metricSourceService from '@/services/metricSourceService';
import {
  applyDerivedStatus,
  applyStatusWrite,
  buildProgressHistoryEntry,
  calculateKrProgress,
  findGoalIndexInGoals,
  pickGoalsTeamDocIndex,
  revertKrStatusToAuto,
} from './utils/krUpdateRules';
// Goals Store Module
// Simple team goals management - stores goals as text on team records

// Goal tag surfaces (mobile Focus Summary) render short_name at ~32 chars
// before ellipsizing. A title that fits IS its own shorthand; a longer title
// leaves short_name absent so the AI backfill can write a proper 2-4 word
// name (a truncated one stored here would block that). Title edits must
// refresh or remove short_name so it can never describe a stale title.
const SHORT_NAME_MAX_CHARS = 32;
function deriveShortName(title: string | undefined | null): string | null {
  const trimmed = (title || '').trim();
  return trimmed && trimmed.length <= SHORT_NAME_MAX_CHARS ? trimmed : null;
}

interface GoalsState {
  editingTeamId: string | null;
  selectedTeam: string;
  searchKeyword: string;
  goalsTeamAI: any;
  goalsLoaded: boolean;
  goalsLoading: boolean;
  showArchivedGoals: boolean;
  suggestedKrLoading: boolean;
  suggestedKrResult: SuggestedKrResult | null;
  suggestedKrGoalId: string | null;
  goalsUnsubscribe: (() => void) | null;
  connectedAccountsByToolkit: Record<string, metricSourceService.ConnectedAccount[]>;
  toolkitCatalog: metricSourceService.Toolkit[];
  toolkitCatalogLoadedAt: number;
  toolkitCatalogLoading: boolean;
  composer: {
    activeRecipe: metricSourceService.ComposeRecipeResponse | null;
    activePreview: metricSourceService.PreviewResponse | null;
    isComposing: boolean;
    isPreviewing: boolean;
  };
  connectedAccountsUnsubscribers: Record<string, () => void>;
}

interface KeyResult {
  kr_id: string
  title: string
  type?: string
  start_value: number
  target_value: number                     // Team-level target (sum owners are chasing together in individual mode)
  current_value: number                    // Used in collaborative mode (shared progress)
  unit: string
  owner_emails: string[]
  mode: 'collaborative' | 'individual'     // How progress is tracked across owners
  owner_progress: Record<string, number>   // Used in individual mode: { email: value }
  owner_targets?: Record<string, number>   // Used in individual mode: { email: target } (sparse — missing keys default to even split)
  cycle: string                            // e.g. 'Q1 2026', 'H1 2026', '' for evergreen
  status: 'on_track' | 'at_risk' | 'off_track' | 'completed'
  // 'manual' ⇒ a human set the status deliberately via the dropdown, and value
  // updates must not overwrite it (except derived completion, which clears it).
  // Absent ⇒ status is derived from progress. See utils/krUpdateRules (contract C3).
  status_source?: 'manual'
  progress_history: Array<{
    value: number
    updated_at: string
    updated_by: string
    note: string
    // Optional user-declared status override. Set only when the entry was created by a
    // manual status change (see updateKeyResultStatus). Absent on value-only progress entries,
    // where status is derived from progress on the fly.
    status?: 'on_track' | 'at_risk' | 'off_track' | 'completed'
  }>
  created_at: string
}

// Even-split default when an owner has no custom target override.
// The last owner absorbs the integer remainder so defaults sum to target_value.
export function defaultOwnerTarget(targetValue: number, ownerCount: number, ownerIndex: number): number {
  if (!ownerCount || ownerCount <= 0) return targetValue;
  if (!Number.isFinite(targetValue)) return 0;
  const isInteger = Number.isInteger(targetValue);
  if (!isInteger) {
    // Preserve decimals — equal split.
    return targetValue / ownerCount;
  }
  const base = Math.floor(targetValue / ownerCount);
  const remainder = targetValue - base * ownerCount;
  return ownerIndex === ownerCount - 1 ? base + remainder : base;
}

// Compute the effective per-owner target map for a KR's current state.
// Customized rows (keys present in overrides) keep their stored values.
// Uncustomized rows default to a plain even-split of the team target — they do NOT
// auto-absorb the delta from customized rows, because doing so would silently move
// values the user hasn't touched. Divergence between the sum and the team target is
// surfaced to the user via the roll-up warning instead, and the caller offers an
// explicit "Match team target to sum" action to resolve it.
export function computeOwnerTargetMap(
  ownerEmails: string[] | undefined,
  teamTarget: number,
  overrides: Record<string, number> | undefined,
): Record<string, number> {
  const emails = ownerEmails || [];
  const target = Number.isFinite(teamTarget) ? teamTarget : 0;
  const safeOverrides = overrides || {};
  const map: Record<string, number> = {};
  emails.forEach((email, i) => {
    if (Object.prototype.hasOwnProperty.call(safeOverrides, email)) {
      map[email] = safeOverrides[email];
    } else {
      map[email] = defaultOwnerTarget(target, emails.length, i);
    }
  });
  return map;
}

// Resolve an owner's effective target, applying the same rebalancing as the UI.
export function getOwnerTarget(kr: { target_value: number; owner_emails?: string[]; owner_targets?: Record<string, number> }, email: string): number {
  const map = computeOwnerTargetMap(kr.owner_emails, kr.target_value, kr.owner_targets);
  return map[email] || 0;
}

export interface OwnerTargetRollup {
  sum: number;
  delta: number;    // sum - teamTarget; negative = short, positive = over
  matches: boolean; // true when |delta| is within float tolerance
}

export function computeOwnerTargetRollup(
  ownerEmails: string[] | undefined,
  teamTarget: number,
  overrides: Record<string, number> | undefined,
): OwnerTargetRollup {
  const map = computeOwnerTargetMap(ownerEmails, teamTarget, overrides);
  const sum = Object.values(map).reduce((s, v) => s + (Number(v) || 0), 0);
  const team = Number.isFinite(teamTarget) ? teamTarget : 0;
  const delta = sum - team;
  return { sum, delta, matches: Math.abs(delta) < 0.0001 };
}

// Write every row's currently-displayed value as an explicit override, so subsequent
// team-target changes don't reflow uncustomized rows.
export function freezeOwnerTargetsToEffective(
  ownerEmails: string[] | undefined,
  overrides: Record<string, number> | undefined,
  effective: Record<string, number>,
): Record<string, number> {
  const frozen: Record<string, number> = { ...(overrides || {}) };
  for (const email of ownerEmails || []) {
    if (!Object.prototype.hasOwnProperty.call(frozen, email)) {
      frozen[email] = effective[email];
    }
  }
  return frozen;
}

// Drop entries for emails no longer in the allowed set. Returns a new object.
export function pruneOwnerMap(
  map: Record<string, number> | undefined,
  allowedEmails: string[] | undefined,
): Record<string, number> {
  const allowed = new Set(allowedEmails || []);
  const src = map || {};
  const next: Record<string, number> = {};
  for (const email of Object.keys(src)) {
    if (allowed.has(email)) next[email] = src[email];
  }
  return next;
}

// Individual mode sums per-owner progress, so a target below the start value
// (e.g. "reduce churn 30% → 20%") can't be meaningfully split across owners.
// Collaborative mode handles decreasing targets fine, so this flag gates UX
// warnings specific to the individual tracking case.
export function isDecreasingIndividualKr(fields: {
  mode?: string;
  owner_emails?: string[] | null;
  start_value?: number | null;
  target_value?: number | null;
}): boolean {
  if (fields.mode !== 'individual') return false;
  if (!fields.owner_emails || fields.owner_emails.length <= 1) return false;
  const start = Number(fields.start_value);
  const target = Number(fields.target_value);
  if (!Number.isFinite(start) || !Number.isFinite(target)) return false;
  return target < start;
}

// Round to at most 2 decimals for display; integers stay integers.
export function formatTargetNumber(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 100) / 100);
}

interface SuggestedKrResult {
  suggestedKeyResults: Array<{
    title: string;
    type?: string;
    start_value: number;
    target_value: number;
    unit: string;
    reasoning: string;
    confidence: 'high' | 'medium' | 'low';
  }>;
  generatedAt: string;
  suggestionId?: string;
  cachedAt?: string;
  error?: string;
}

/**
 * Contract C1 — resolve the goals_team_ai doc for a team by FIELD QUERY only,
 * never by constructing a doc ID (legacy `${companyId}--${teamId}` docs coexist
 * with canonical `${companyId}-${teamId}` ones). When multiple docs match,
 * pickGoalsTeamDocIndex prefers the doc containing `goalId`, then the first doc
 * with a non-empty goals array.
 */
async function resolveGoalsTeamDoc(companyId: string, teamId: string, goalId?: string | null): Promise<any | null> {
  const snapshot = await firebase.firestore()
    .collection('goals_team_ai')
    .where('company_id', '==', companyId)
    .where('team_id', '==', teamId)
    .get();

  if (snapshot.empty) return null;

  const docs = snapshot.docs.map((doc: any) => {
    const data = doc.data() || {};
    return { id: doc.id, goals: data.goals };
  });
  const idx = pickGoalsTeamDocIndex(docs, goalId || undefined);
  return idx === -1 ? null : snapshot.docs[idx];
}

/**
 * Contract C2 — every mutation of goals[] is read-modify-write inside a
 * Firestore transaction:
 *   1. resolve the doc ref by field query (outside the transaction),
 *   2. transaction.get(docRef) for fresh data,
 *   3. apply a pure updater (goals) => updatedGoals to the FRESH array,
 *   4. transaction.update(docRef, { goals, updatedAt }).
 * The updater returns null to signal the goal/KR no longer exists in the fresh
 * data — no write happens. In-place mutation inside the updater is safe:
 * fresh.data() materializes new objects on every call, so a retried
 * transaction re-runs the updater against untouched data.
 */
async function updateGoalsTeamDoc(
  companyId: string,
  teamId: string,
  goalId: string | null,
  updater: (goals: any[], ctx: { docId: string }) => any[] | null,
): Promise<{ success: boolean; error?: any }> {
  const targetDoc = await resolveGoalsTeamDoc(companyId, teamId, goalId);
  if (!targetDoc) return { success: false, error: 'Goal not found' };
  const docRef = targetDoc.ref;

  let notFound = false;
  await firebase.firestore().runTransaction(async (transaction: any) => {
    notFound = false; // transactions retry — reset per attempt
    const fresh = await transaction.get(docRef);
    if (!fresh.exists) {
      notFound = true;
      return;
    }
    const data = fresh.data() || {};
    const goals = Array.isArray(data.goals) ? data.goals : [];
    const updatedGoals = updater(goals, { docId: fresh.id });
    if (updatedGoals === null) {
      notFound = true;
      return;
    }
    transaction.update(docRef, {
      goals: updatedGoals,
      updatedAt: new Date().toISOString(),
    });
  });

  if (notFound) return { success: false, error: 'Goal not found' };
  return { success: true };
}

/**
 * Generate fallback KR suggestions when the AI endpoint is not yet deployed.
 * Produces context-aware suggestions based on the goal's title and description.
 * This will be removed once /ai/suggest_key_results is live.
 */
function generateFallbackKrSuggestions(goal: { quick_preview: string; goal_description: string; existing_key_results?: any[] }): SuggestedKrResult {
  const title = (goal.quick_preview || '').toLowerCase();
  const desc = (goal.goal_description || '').toLowerCase();
  const combined = title + ' ' + desc;

  const suggestions: SuggestedKrResult['suggestedKeyResults'] = [];

  // Pattern-match common goal themes to generate relevant KRs.
  // User-facing strings (title, reasoning, unit) are resolved through
  // the current i18n dictionary at generation time.
  if (combined.match(/revenue|sales|grow|income|arr|mrr/)) {
    suggestions.push(
      { title: t('goals.kr_templates.revenue_mrr_title'), start_value: 0, target_value: 50000, unit: '$', reasoning: t('goals.kr_templates.revenue_mrr_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.revenue_deals_title'), start_value: 0, target_value: 20, unit: t('goals.kr_templates.unit_deals'), reasoning: t('goals.kr_templates.revenue_deals_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.revenue_deal_size_title'), start_value: 0, target_value: 5000, unit: '$', reasoning: t('goals.kr_templates.revenue_deal_size_reasoning'), confidence: 'medium' },
    );
  } else if (combined.match(/customer|satisfaction|nps|csat|retention|churn/)) {
    suggestions.push(
      { title: t('goals.kr_templates.customer_nps_title'), start_value: 30, target_value: 60, unit: '', reasoning: t('goals.kr_templates.customer_nps_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.customer_churn_title'), start_value: 8, target_value: 4, unit: '%', reasoning: t('goals.kr_templates.customer_churn_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.customer_support_title'), start_value: 70, target_value: 95, unit: t('goals.kr_templates.unit_percent_within_sla'), reasoning: t('goals.kr_templates.customer_support_reasoning'), confidence: 'medium' },
    );
  } else if (combined.match(/hire|recruit|team|headcount|onboard/)) {
    suggestions.push(
      { title: t('goals.kr_templates.hire_fill_title'), start_value: 0, target_value: 5, unit: t('goals.kr_templates.unit_hires'), reasoning: t('goals.kr_templates.hire_fill_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.hire_time_title'), start_value: 45, target_value: 30, unit: t('goals.kr_templates.unit_days'), reasoning: t('goals.kr_templates.hire_time_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.hire_retention_title'), start_value: 80, target_value: 95, unit: '%', reasoning: t('goals.kr_templates.hire_retention_reasoning'), confidence: 'medium' },
    );
  } else if (combined.match(/ship|launch|release|feature|product|build|deliver/)) {
    suggestions.push(
      { title: t('goals.kr_templates.ship_features_title'), start_value: 0, target_value: 5, unit: t('goals.kr_templates.unit_features'), reasoning: t('goals.kr_templates.ship_features_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.ship_bugs_title'), start_value: 15, target_value: 5, unit: '%', reasoning: t('goals.kr_templates.ship_bugs_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.ship_adoption_title'), start_value: 0, target_value: 40, unit: t('goals.kr_templates.unit_percent_of_users'), reasoning: t('goals.kr_templates.ship_adoption_reasoning'), confidence: 'medium' },
    );
  } else if (combined.match(/engage|culture|happiness|wellbeing|morale/)) {
    suggestions.push(
      { title: t('goals.kr_templates.engage_score_title'), start_value: 65, target_value: 80, unit: '', reasoning: t('goals.kr_templates.engage_score_reasoning'), confidence: 'high' },
      { title: t('goals.kr_templates.engage_participation_title'), start_value: 60, target_value: 90, unit: '%', reasoning: t('goals.kr_templates.engage_participation_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.engage_turnover_title'), start_value: 12, target_value: 6, unit: '%', reasoning: t('goals.kr_templates.engage_turnover_reasoning'), confidence: 'medium' },
    );
  } else {
    // Generic fallback
    suggestions.push(
      { title: t('goals.kr_templates.generic_milestones_title'), start_value: 0, target_value: 5, unit: t('goals.kr_templates.unit_milestones'), reasoning: t('goals.kr_templates.generic_milestones_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.generic_completion_title'), start_value: 0, target_value: 100, unit: '%', reasoning: t('goals.kr_templates.generic_completion_reasoning'), confidence: 'medium' },
      { title: t('goals.kr_templates.generic_improvement_title'), start_value: 0, target_value: 10, unit: '', reasoning: t('goals.kr_templates.generic_improvement_reasoning'), confidence: 'low' },
    );
  }

  // Filter out KRs that overlap with existing ones by title similarity
  const existingTitles = (goal.existing_key_results || []).map((kr: any) => (kr.title || '').toLowerCase());
  const filtered = suggestions.filter(s => {
    const sTitle = s.title.toLowerCase();
    return !existingTitles.some((et: string) => et === sTitle || (et.length > 10 && sTitle.includes(et)));
  });

  return {
    suggestedKeyResults: filtered.length > 0 ? filtered : suggestions.slice(0, 2),
    generatedAt: new Date().toISOString(),
  };
}

function parseGoalsSnapshot(snapshot: any): Record<string, any[]> {
  const goals_team_ai: Record<string, any[]> = {};
  snapshot.docs.forEach((doc: any) => {
    const data = doc.data();
    const docId = doc.id;

    if (!goals_team_ai[data.team_id]) {
      goals_team_ai[data.team_id] = [];
    }

    if (data.goals && Array.isArray(data.goals)) {
      data.goals.forEach((goal: any, index: number) => {
        goals_team_ai[data.team_id].push({
          goal_id: goal.goal_id_ai || `${docId}_${index}`,
          quick_preview: goal.quick_preview || '',
          goal_description: goal.goal_description || goal.quick_preview || '',
          archived: goal.archived || false,
          archivedAt: goal.archivedAt || null,
          parent_goal_ids: goal.parent_goal_ids || [],
          key_results: (goal.key_results || []).map((kr: any) => ({
            ...kr,
            owner_emails: kr.owner_emails || (kr.owner_email ? [kr.owner_email] : []),
            mode: kr.mode || 'collaborative',
            owner_progress: kr.owner_progress || {},
            owner_targets: kr.owner_targets || {},
            cycle: kr.cycle || '',
          })),
          teamId: data.team_id,
        });
      });
    } else if (data.goal_id || data.quick_preview) {
      goals_team_ai[data.team_id].push({
        goal_id: data.goal_id_ai || docId,
        quick_preview: data.quick_preview || '',
        goal_description: data.goal_description || data.quick_preview || '',
        archived: data.archived || false,
        archivedAt: data.archivedAt || null,
        parent_goal_ids: data.parent_goal_ids || [],
        key_results: [],
        teamId: data.team_id,
      });
    }
  });
  return goals_team_ai;
}

const state: GoalsState = {
  editingTeamId: null,
  selectedTeam: 'All Teams',
  searchKeyword: '',
  goalsTeamAI: {},
  goalsLoaded: false,
  goalsLoading: false,
  showArchivedGoals: false,
  suggestedKrLoading: false,
  suggestedKrResult: null,
  suggestedKrGoalId: null,
  goalsUnsubscribe: null,
  connectedAccountsByToolkit: {},
  toolkitCatalog: [],
  toolkitCatalogLoadedAt: 0,
  toolkitCatalogLoading: false,
  composer: {
    activeRecipe: null,
    activePreview: null,
    isComposing: false,
    isPreviewing: false,
  },
  connectedAccountsUnsubscribers: {},
};

const mutations = {
  setEditingTeamId(state: GoalsState, teamId: string | null) {
    state.editingTeamId = teamId;
  },
  setSelectedTeam(state: GoalsState, teamId: string) {
    state.selectedTeam = teamId;
  },
  setSearchKeyword(state: GoalsState, keyword: string) {
    state.searchKeyword = keyword;
  },
  setGoalsTeamAI(state: GoalsState, goalsTeamAI: any) {
    // Use Object.assign to ensure Vue reactivity detects the change
    state.goalsTeamAI = Object.assign({}, goalsTeamAI);
  },
  setGoalsLoaded(state: GoalsState, loaded: boolean) {
    state.goalsLoaded = loaded;
  },
  setGoalsLoading(state: GoalsState, loading: boolean) {
    state.goalsLoading = loading;
  },
  setShowArchivedGoals(state: GoalsState, show: boolean) {
    state.showArchivedGoals = show;
  },
  setSuggestedKrLoading(state: GoalsState, value: boolean) {
    state.suggestedKrLoading = value;
  },
  setSuggestedKrResult(state: GoalsState, result: SuggestedKrResult | null) {
    state.suggestedKrResult = result;
  },
  setSuggestedKrGoalId(state: GoalsState, goalId: string | null) {
    state.suggestedKrGoalId = goalId;
  },
  setGoalsUnsubscribe(state: GoalsState, fn: (() => void) | null) {
    state.goalsUnsubscribe = fn;
  },
  setConnectedAccounts(
    state: GoalsState,
    payload: { toolkitSlug: string; accounts: metricSourceService.ConnectedAccount[] }
  ) {
    state.connectedAccountsByToolkit = {
      ...state.connectedAccountsByToolkit,
      [payload.toolkitSlug]: payload.accounts,
    };
  },
  setToolkitCatalog(state: GoalsState, toolkits: metricSourceService.Toolkit[]) {
    state.toolkitCatalog = toolkits;
    state.toolkitCatalogLoadedAt = Date.now();
  },
  setToolkitCatalogLoading(state: GoalsState, loading: boolean) {
    state.toolkitCatalogLoading = loading;
  },
  setComposerRecipe(state: GoalsState, recipe: metricSourceService.ComposeRecipeResponse | null) {
    state.composer = { ...state.composer, activeRecipe: recipe };
  },
  setComposerPreview(state: GoalsState, preview: metricSourceService.PreviewResponse | null) {
    state.composer = { ...state.composer, activePreview: preview };
  },
  setComposerComposing(state: GoalsState, value: boolean) {
    state.composer = { ...state.composer, isComposing: value };
  },
  setComposerPreviewing(state: GoalsState, value: boolean) {
    state.composer = { ...state.composer, isPreviewing: value };
  },
  resetComposer(state: GoalsState) {
    state.composer = {
      activeRecipe: null,
      activePreview: null,
      isComposing: false,
      isPreviewing: false,
    };
  },
  setConnectedAccountsUnsubscriber(state: GoalsState, payload: { toolkitSlug: string; unsub: (() => void) | null }) {
    const next = { ...state.connectedAccountsUnsubscribers };
    if (payload.unsub) {
      next[payload.toolkitSlug] = payload.unsub;
    } else {
      delete next[payload.toolkitSlug];
    }
    state.connectedAccountsUnsubscribers = next;
  },
};

const actions = {
  openEditModal({ commit }: any, teamId: string) {
    commit('setEditingTeamId', teamId);
  },

  closeEditModal({ commit }: any) {
    commit('setEditingTeamId', null);
  },

  async getGoalsTeamAI({ commit, rootState }: any) {
    commit('setGoalsLoading', true);
    try {
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', rootState.authen.user.company)
        .get();
      commit('setGoalsTeamAI', { ...parseGoalsSnapshot(snapshot) });
      commit('setGoalsLoaded', true);
    } finally {
      commit('setGoalsLoading', false);
    }
  },

  subscribeGoalsTeamAI({ commit, state, rootState }: any) {
    // Avoid duplicate listeners
    if (state.goalsUnsubscribe) return;

    commit('setGoalsLoading', true);
    const unsubscribe = firebase.firestore()
      .collection('goals_team_ai')
      .where('company_id', '==', rootState.authen.user.company)
      .onSnapshot((snapshot: any) => {
        commit('setGoalsTeamAI', { ...parseGoalsSnapshot(snapshot) });
        commit('setGoalsLoaded', true);
        commit('setGoalsLoading', false);
      }, (error: any) => {
        commit('setGoalsLoading', false);
        console.error('Goals snapshot listener error:', error);
      });

    commit('setGoalsUnsubscribe', unsubscribe);
  },

  unsubscribeGoalsTeamAI({ commit, state }: any) {
    if (state.goalsUnsubscribe) {
      state.goalsUnsubscribe();
      commit('setGoalsUnsubscribe', null);
    }
  },

  async updateSingleGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, quick_preview, goal_description }: { teamId: string; goalId: string; quick_preview?: string; goal_description?: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          if (quick_preview !== undefined) {
            updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], quick_preview };
          }
          if (goal_description !== undefined) {
            updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], goal_description };
          }
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        if (quick_preview !== undefined) {
          updatedGoals[goalIndex].quick_preview = quick_preview;
          const shortName = deriveShortName(quick_preview);
          if (shortName) updatedGoals[goalIndex].short_name = shortName;
          else delete updatedGoals[goalIndex].short_name;
        }
        if (goal_description !== undefined) {
          updatedGoals[goalIndex].goal_description = goal_description;
        }
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error updating single goal:', error);
      return { success: false, error };
    }
  },

  async saveTeamGoals({ commit, rootGetters, dispatch }: any, { teamId, goals }: { teamId: string; goals: string }) {
    // TODO: Backend - AI will parse and structure goals into individual items with labels and descriptions
    // For now, save raw text to team.goals field
    try {
      // Get teams from team store and update
      const teams = rootGetters['team/teamsall'] || [];
      const team = teams.find((t: any) => t.key === teamId);

      if (team) {
        team.goals = goals;
      }

      firebase.database().ref(`team/${teamId}`).update({
        goals: goals
      })

      // TODO: Call API to persist to backend
      // const response = await updateTeamGoals(teamId, goals);
      await dispatch('main/addAndUpdateGoalTeam',{teamId:teamId, goals:goals}, {root:true});

      commit('setEditingTeamId', null);

      return { success: true };
    } catch (error) {
      console.error('Error saving team goals:', error);
      return { success: false, error };
    }
  },

  /**
   * Save structured goals directly without AI transformation
   * Used by the "Advanced" tab in the goals modal
   * Handles both updating existing goals and adding new ones
   */
  async saveStructuredGoals(
    { commit, rootState, dispatch }: any,
    { teamId, goals }: { teamId: string; goals: Array<{ goal_id?: string; title: string; description: string; parent_goal_ids?: string[] }> }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      
      // Filter out empty goals (no title)
      const validGoals = goals.filter(g => g.title && g.title.trim() !== '');
      
      if (validGoals.length === 0) {
        return { success: false, error: 'No valid goals to save' };
      }

      // Check if document already exists for this team
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      // Separate existing goals (to update) from new goals (to add)
      const goalsToUpdate = validGoals.filter(g => g.goal_id);
      const goalsToAdd = validGoals.filter(g => !g.goal_id);

      // Generate structured goals for new entries
      const newStructuredGoals = goalsToAdd.map((goal) => {
        const shortName = deriveShortName(goal.title);
        return {
          goal_id_ai: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          quick_preview: goal.title.trim(),
          ...(shortName ? { short_name: shortName } : {}),
          goal_description: (goal.description && goal.description.trim()) || goal.title.trim(),
          parent_goal_ids: goal.parent_goal_ids || [],
          archived: false,
          archivedAt: null,
          createdAt: new Date().toISOString(),
          source: 'manual', // Indicate this was manually entered, not AI-generated
        };
      });

      let updatedCount = 0;
      const addedCount = newStructuredGoals.length;
      const nowIso = new Date().toISOString();

      if (snapshot.empty) {
        // Contract C1: creation only happens after the field query found no doc
        // (either ID format), and new docs use the canonical single-dash ID.
        // The transaction guards the same-ID race: if a parallel writer created
        // the doc between the query and here, append instead of overwriting.
        const docRef = firebase.firestore().collection('goals_team_ai').doc(`${companyId}-${teamId}`);
        const goalTeamId = firebase.firestore().collection('goals_team_ai').doc().id;
        await firebase.firestore().runTransaction(async (transaction: any) => {
          const fresh = await transaction.get(docRef);
          if (fresh.exists) {
            const data = fresh.data() || {};
            const existingGoals = Array.isArray(data.goals) ? data.goals : [];
            transaction.update(docRef, {
              goals: [...existingGoals, ...newStructuredGoals],
              updatedAt: nowIso,
            });
          } else {
            transaction.set(docRef, {
              company_id: companyId,
              team_id: teamId,
              goal_team_id: goalTeamId,
              goals: newStructuredGoals,
              createdAt: nowIso,
              updatedAt: nowIso,
            });
          }
        });
      } else {
        // Existing doc (either ID format): append/update via the shared
        // transactional helper. Count matches against the pre-read snapshot so
        // the toast message doesn't depend on transaction retries.
        const preDocs = snapshot.docs.map((d: any) => ({ id: d.id, goals: (d.data() || {}).goals }));
        const preIdx = pickGoalsTeamDocIndex(preDocs);
        const preGoals: any[] = (preIdx !== -1 && Array.isArray(preDocs[preIdx].goals)) ? preDocs[preIdx].goals : [];
        updatedCount = goalsToUpdate.filter(g => preGoals.some((eg: any) => eg && eg.goal_id_ai === g.goal_id)).length;

        const result = await updateGoalsTeamDoc(companyId, teamId, null, (goals) => {
          const merged = goals.map((existingGoal: any) => {
            const updateData = goalsToUpdate.find(g => g.goal_id === existingGoal.goal_id_ai);
            if (!updateData) return existingGoal;
            const shortName = deriveShortName(updateData.title);
            const updatedGoal: any = {
              ...existingGoal,
              quick_preview: updateData.title.trim(),
              goal_description: (updateData.description && updateData.description.trim()) || updateData.title.trim(),
              parent_goal_ids: updateData.parent_goal_ids || existingGoal.parent_goal_ids || [],
              updatedAt: new Date().toISOString(),
            };
            if (shortName) updatedGoal.short_name = shortName;
            else delete updatedGoal.short_name;
            return updatedGoal;
          });
          return [...merged, ...newStructuredGoals];
        });
        if (!result.success) {
          return result;
        }
      }

      // Refresh local state

      commit('setEditingTeamId', null);

      return {
        success: true,
        goalsAdded: addedCount,
        goalsUpdated: updatedCount,
        newGoals: newStructuredGoals, // Return newly created goals with their IDs
        message: `${updatedCount > 0 ? `${updatedCount} updated` : ''}${updatedCount > 0 && addedCount > 0 ? ', ' : ''}${addedCount > 0 ? `${addedCount} added` : ''}`
      };
    } catch (error) {
      console.error('Error saving structured goals:', error);
      return { success: false, error };
    }
  },

  /**
   * Archive a goal (soft-delete)
   * Sets archived: true and archivedAt timestamp
   */
  async archiveGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };
    const archivedAt = new Date().toISOString();

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], archived: true, archivedAt };
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        updatedGoals[goalIndex].archived = true;
        updatedGoals[goalIndex].archivedAt = archivedAt;
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error archiving goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Unarchive a goal (restore from archive)
   * Sets archived: false
   */
  async unarchiveGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const { archivedAt, ...goalWithoutArchivedAt } = updatedTeamGoals[goalIndex];
          updatedTeamGoals[goalIndex] = { ...goalWithoutArchivedAt, archived: false };
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        updatedGoals[goalIndex].archived = false;
        delete updatedGoals[goalIndex].archivedAt;
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error unarchiving goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Permanently delete a goal
   * Removes the goal from the goals array
   */
  async deleteGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const updatedTeamGoals = teamGoals.filter((g: any) => g.goal_id !== goalId);
        const newGoalsTeamAI: Record<string, any[]> = {};
        Object.keys(state.goalsTeamAI).forEach((key) => {
          if (key === teamId) {
            if (updatedTeamGoals.length > 0) {
              newGoalsTeamAI[key] = updatedTeamGoals;
            }
          } else {
            newGoalsTeamAI[key] = state.goalsTeamAI[key];
          }
        });
        commit('setGoalsTeamAI', newGoalsTeamAI);
      }

      const companyId = rootState.authen.user.company;
      let remainingActiveCount = 0;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        updatedGoals.splice(goalIndex, 1);
        remainingActiveCount = updatedGoals.filter((g: any) => !g.archived).length;
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      // If all goals for this team are now deleted, clear the legacy team.goals string in RTDB
      if (remainingActiveCount === 0) {
        firebase.database().ref(`team/${teamId}`).update({ goals: '' });
      }

      // Snapshot listener will pick up the Firestore changes automatically
      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error deleting goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Get ancestor teams for a given team (traverses up the parent chain)
   * Returns array of teams from direct parent to root
   */
  getAncestorTeams({ rootGetters }: any, teamId: string): any[] {
    const allTeams = rootGetters['team/teamsall'] || [];
    const ancestors: any[] = [];
    
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    return ancestors;
  },

  /**
   * Get all goals from ancestor teams for linking purposes
   * Returns goals grouped by team with team metadata
   */
  getAncestorTeamGoals({ state, dispatch, rootGetters }: any, teamId: string): any[] {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Get ancestor teams
    const ancestors: any[] = [];
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    // For each ancestor team, get their goals
    ancestors.forEach((team: any) => {
      const teamGoals = goalsTeamAI[team.key] || [];
      // Only include non-archived goals for linking
      const activeGoals = teamGoals.filter((g: any) => !g.archived);
      
      if (activeGoals.length > 0) {
        result.push({
          team: {
            key: team.key,
            name: team.name,
            color: team.color || '#747474',
          },
          goals: activeGoals.map((goal: any) => ({
            goal_id: goal.goal_id,
            quick_preview: goal.quick_preview,
            goal_description: goal.goal_description,
            teamId: team.key,
            teamName: team.name,
            teamColor: team.color || '#747474',
          })),
        });
      }
    });
    
    return result;
  },

  /**
   * Update parent goal links for a goal
   * Sets the parent_goal_ids array for the specified goal
   */
  async updateGoalParentLinks(
    { rootState, dispatch }: any,
    { teamId, goalId, parentGoalIds }: { teamId: string; goalId: string; parentGoalIds: string[] }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        updatedGoals[goalIndex].parent_goal_ids = parentGoalIds;
        return updatedGoals;
      });

      if (!result.success) {
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating goal parent links:', error);
      return { success: false, error };
    }
  },

  /**
   * Add a key result to a goal
   */
  async addKeyResult(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, keyResult }: { teamId: string; goalId: string; keyResult: KeyResult }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const existingKRs = updatedTeamGoals[goalIndex].key_results || [];
          updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], key_results: [...existingKRs, keyResult] };
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        const existingKRs = updatedGoals[goalIndex].key_results || [];
        updatedGoals[goalIndex].key_results = [...existingKRs, keyResult];
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error adding key result:', error);
      return { success: false, error };
    }
  },

  async updateKeyResultFields(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, krId, fields }: {
      teamId: string; goalId: string; krId: string;
      fields: {
        title?: string;
        type?: string;
        start_value?: number;
        target_value?: number;
        unit?: string;
        cycle?: string;
        mode?: 'collaborative' | 'individual';
        owner_targets?: Record<string, number>;
        metric_source?: metricSourceService.MetricSourcePayload | null;
      };
    }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const goal = teamGoals[goalIndex];
          if (goal.key_results && Array.isArray(goal.key_results)) {
            const krIndex = goal.key_results.findIndex((kr: any) => kr.kr_id === krId);
            if (krIndex !== -1) {
              const updatedKrs = [...goal.key_results];
              updatedKrs[krIndex] = { ...updatedKrs[krIndex], ...fields };
              const updatedTeamGoals = [...teamGoals];
              updatedTeamGoals[goalIndex] = { ...goal, key_results: updatedKrs };
              commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
            }
          }
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        const goal = updatedGoals[goalIndex];

        if (goal.key_results && Array.isArray(goal.key_results)) {
          const krIndex = goal.key_results.findIndex((kr: any) => kr.kr_id === krId);
          if (krIndex !== -1) {
            Object.assign(goal.key_results[krIndex], fields);
          }
        }
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        return result;
      }
      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error updating KR fields:', error);
      return { success: false, error };
    }
  },

  async updateKeyResultProgress(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, krId, currentValue, updatedBy, note }: { teamId: string; goalId: string; krId: string; currentValue: number; updatedBy: string; note: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const goal = { ...updatedTeamGoals[goalIndex] };
          const krs = (goal.key_results || []).map((kr: any) => {
            if (kr.kr_id === krId) {
              const updatedKr = { ...kr };
              if (kr.mode === 'individual' && updatedBy) {
                updatedKr.owner_progress = { ...(kr.owner_progress || {}), [updatedBy]: currentValue };
              } else {
                updatedKr.current_value = currentValue;
              }
              // Contract C3: a manually-set status survives value updates
              // (derived completion overrides it and clears the override).
              applyDerivedStatus(updatedKr, calculateKrProgress(updatedKr));
              updatedKr.progress_history = [...(kr.progress_history || []), buildProgressHistoryEntry(updatedKr, { updatedBy, note })];
              return updatedKr;
            }
            return kr;
          });
          goal.key_results = krs;
          updatedTeamGoals[goalIndex] = goal;
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        const krIndex = (updatedGoals[goalIndex].key_results || []).findIndex((kr: any) => kr.kr_id === krId);
        if (krIndex === -1) return null;

        const kr = updatedGoals[goalIndex].key_results[krIndex];
        if (kr.mode === 'individual' && updatedBy) {
          if (!kr.owner_progress) kr.owner_progress = {};
          kr.owner_progress[updatedBy] = currentValue;
        } else {
          kr.current_value = currentValue;
        }
        // Contract C3: a manually-set status survives value updates
        // (derived completion overrides it and clears the override).
        applyDerivedStatus(kr, calculateKrProgress(kr));
        kr.progress_history = [...(kr.progress_history || []), buildProgressHistoryEntry(kr, { updatedBy, note })];
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        return result;
      }
      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error updating key result progress:', error);
      return { success: false, error };
    }
  },

  /**
   * Update status on a key result.
   *
   * `status` accepts the four concrete statuses, or 'auto' — the dropdown's
   * "Auto" option — which removes the manual override (status_source) and
   * re-derives the status from current progress (contract C3).
   */
  async updateKeyResultStatus(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, krId, status, updatedBy }: { teamId: string; goalId: string; krId: string; status: 'on_track' | 'at_risk' | 'off_track' | 'completed' | 'auto'; updatedBy?: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const goal = { ...updatedTeamGoals[goalIndex] };
          const krs = (goal.key_results || []).map((kr: any) => {
            if (kr.kr_id !== krId) return kr;
            const copy = { ...kr };
            if (status === 'auto') {
              revertKrStatusToAuto(copy);
            } else {
              // A human pick from the dropdown is a deliberate judgment —
              // mark it manual so value updates don't overwrite it (C3).
              applyStatusWrite(copy, status, Boolean(updatedBy));
            }
            return copy;
          });
          goal.key_results = krs;
          updatedTeamGoals[goalIndex] = goal;
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        const krIndex = (updatedGoals[goalIndex].key_results || []).findIndex((kr: any) => kr.kr_id === krId);
        if (krIndex === -1) return null;

        const kr = updatedGoals[goalIndex].key_results[krIndex];
        const prevStatus = kr.status;

        let statusForHistory: string | undefined;
        if (status === 'auto') {
          // Drop the manual override and re-derive; only audit if visible status changed.
          const reverted = revertKrStatusToAuto(kr);
          if (Boolean(updatedBy) && reverted.changed) statusForHistory = reverted.status;
        } else {
          applyStatusWrite(kr, status, Boolean(updatedBy));
          // Append a progress_history entry when (a) a real user triggered this
          // (updatedBy present) and (b) the status actually changed.
          // System-driven writes (e.g. rollForwardKeyResults marking rolled-over
          // KRs 'completed') pass no updatedBy, so they skip the audit entry.
          if (Boolean(updatedBy) && prevStatus !== status) statusForHistory = status;
        }

        if (statusForHistory) {
          kr.progress_history = [
            ...(kr.progress_history || []),
            buildProgressHistoryEntry(kr, { updatedBy, status: statusForHistory as any }),
          ];
        }
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error updating key result status:', error);
      return { success: false, error };
    }
  },

  /**
   * Delete a key result from a goal
   */
  async deleteKeyResult(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, krId }: { teamId: string; goalId: string; krId: string }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const goal = { ...updatedTeamGoals[goalIndex] };
          goal.key_results = (goal.key_results || []).filter((kr: any) => kr.kr_id !== krId);
          updatedTeamGoals[goalIndex] = goal;
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        updatedGoals[goalIndex].key_results = (updatedGoals[goalIndex].key_results || []).filter((kr: any) => kr.kr_id !== krId);
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return result;
      }

      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error deleting key result:', error);
      return { success: false, error };
    }
  },

  async suggestKeyResults(
    { commit, rootState, rootGetters }: any,
    { teamId, goalId, goal, focusItems, teamContext }: {
      teamId: string;
      goalId: string;
      goal: {
        goal_id: string;
        quick_preview: string;
        goal_description: string;
        existing_key_results: any[];
      };
      focusItems: Array<{ itemText: string; status: string; count: number }>;
      teamContext: { teamName: string; memberCount: number };
    }
  ): Promise<SuggestedKrResult | null> {
    commit('setSuggestedKrLoading', true);
    commit('setSuggestedKrResult', null);
    commit('setSuggestedKrGoalId', goalId);

    try {
      const company = rootState.authen.user.company;
      const apiUrl = process.env.VUE_APP_API_URL || 'https://api-node.happily.ai';

      let result: SuggestedKrResult;

      try {
        const response = await axios.post(`${apiUrl}/ai/suggest_key_results`, {
          company,
          teamId,
          goal,
          focusItems,
          teamContext,
        });
        result = response.data;
      } catch (apiError) {
        const typedApiError: any = apiError;
        // Fallback to generated suggestions when endpoint is not yet deployed (404)
        if (typedApiError.response && typedApiError.response.status === 404) {
          result = generateFallbackKrSuggestions(goal);
        } else {
          throw typedApiError;
        }
      }

      // Cache in Firestore
      if (result && !result.error) {
        const docRef = await firebase.firestore().collection('suggested_key_results').add({
          company,
          teamId,
          goalId,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          createdAtMs: Date.now(),
          status: 'pending',
          suggestedKeyResults: result.suggestedKeyResults,
          generatedAt: result.generatedAt,
        });
        result.suggestionId = docRef.id;
      }

      commit('setSuggestedKrResult', result);
      return result;
    } catch (error) {
      const typedError: any = error;
      console.error('Error suggesting key results:', error);
      const errorResult: SuggestedKrResult = {
        suggestedKeyResults: [],
        generatedAt: new Date().toISOString(),
        error: typedError.message || 'Failed to suggest key results',
      };
      commit('setSuggestedKrResult', errorResult);
      return errorResult;
    } finally {
      commit('setSuggestedKrLoading', false);
    }
  },

  async loadCachedKrSuggestions(
    { commit, rootState }: any,
    { teamId, goalId, maxAgeHours = 24 }: { teamId: string; goalId: string; maxAgeHours?: number }
  ): Promise<SuggestedKrResult | null> {
    commit('setSuggestedKrLoading', true);

    try {
      const company = rootState.authen.user.company;
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - maxAgeHours);

      const snapshot = await firebase.firestore()
        .collection('suggested_key_results')
        .where('company', '==', company)
        .where('teamId', '==', teamId)
        .where('goalId', '==', goalId)
        .where('status', '==', 'pending')
        .get();

      if (snapshot.empty) {
        commit('setSuggestedKrLoading', false);
        return null;
      }

      // Sort by createdAtMs descending to get most recent
      const sortedDocs = snapshot.docs.sort((a, b) => {
        const aMs = a.data().createdAtMs || 0;
        const bMs = b.data().createdAtMs || 0;
        return bMs - aMs;
      });

      const doc = sortedDocs[0];
      const data = doc.data();

      // Check freshness
      const createdAtMs = data.createdAtMs || 0;
      if (createdAtMs <= 0 || createdAtMs < cutoffTime.getTime()) {
        commit('setSuggestedKrLoading', false);
        return null;
      }

      const result: SuggestedKrResult = {
        suggestedKeyResults: data.suggestedKeyResults || [],
        generatedAt: data.generatedAt || new Date(createdAtMs).toISOString(),
        cachedAt: new Date(createdAtMs).toISOString(),
        suggestionId: doc.id,
      };

      commit('setSuggestedKrResult', result);
      commit('setSuggestedKrGoalId', goalId);
      commit('setSuggestedKrLoading', false);
      return result;
    } catch (error) {
      console.error('Error loading cached KR suggestions:', error);
      commit('setSuggestedKrLoading', false);
      return null;
    }
  },

  async updateKeyResultOwners(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, krId, ownerEmails }: { teamId: string; goalId: string; krId: string; ownerEmails: string[] }
  ) {
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const goal = teamGoals[goalIndex];
          if (goal.key_results && Array.isArray(goal.key_results)) {
            const krIndex = goal.key_results.findIndex((kr: any) => kr.kr_id === krId);
            if (krIndex !== -1) {
              const updatedKrs = [...goal.key_results];
              const existing = updatedKrs[krIndex];
              updatedKrs[krIndex] = {
                ...existing,
                owner_emails: ownerEmails,
                owner_progress: pruneOwnerMap(existing.owner_progress, ownerEmails),
                owner_targets: pruneOwnerMap(existing.owner_targets, ownerEmails),
              };
              const updatedTeamGoals = [...teamGoals];
              updatedTeamGoals[goalIndex] = { ...goal, key_results: updatedKrs };
              commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
            }
          }
        }
      }

      const companyId = rootState.authen.user.company;
      const result = await updateGoalsTeamDoc(companyId, teamId, goalId, (goals, { docId }) => {
        const goalIndex = findGoalIndexInGoals(goals, goalId, docId);
        if (goalIndex === -1) return null;
        const updatedGoals = [...goals];
        const goal = updatedGoals[goalIndex];

        if (goal.key_results && Array.isArray(goal.key_results)) {
          const krIndex = goal.key_results.findIndex((kr: any) => kr.kr_id === krId);
          if (krIndex !== -1) {
            const kr = goal.key_results[krIndex];
            kr.owner_emails = ownerEmails;
            kr.owner_progress = pruneOwnerMap(kr.owner_progress, ownerEmails);
            kr.owner_targets = pruneOwnerMap(kr.owner_targets, ownerEmails);
          }
        }
        return updatedGoals;
      });

      if (!result.success) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        return result;
      }
      return { success: true };
    } catch (error) {
      commit('setGoalsTeamAI', oldGoalsTeamAI);

      console.error('Error updating KR owners:', error);
      return { success: false, error };
    }
  },

  /**
   * Roll forward selected KRs into a new cycle.
   * Archives the originals (sets status to 'completed' with final progress snapshot)
   * and creates copies with reset progress for the new cycle.
   */
  async rollForwardKeyResults(
    { state, dispatch }: any,
    { teamId, goalId, krIds, newCycle }: { teamId: string; goalId: string; krIds: string[]; newCycle: string }
  ) {
    try {
      // Read KR data from state BEFORE archiving
      const teamGoals = state.goalsTeamAI[teamId] || [];
      const goal = teamGoals.find((g: any) => g.goal_id === goalId);
      if (!goal) return { success: false, error: 'Goal not found' };

      const oldKrs = (goal.key_results || []).filter((kr: any) => krIds.includes(kr.kr_id));

      // Mark originals as completed
      for (const kr of oldKrs) {
        await dispatch('updateKeyResultStatus', { teamId, goalId, krId: kr.kr_id, status: 'completed' });
      }

      // Create fresh copies with reset progress for the new cycle
      const newKrs: KeyResult[] = [];
      for (const oldKr of oldKrs) {
        const newKr: KeyResult = {
          kr_id: 'kr_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
          title: oldKr.title,
          type: oldKr.type,
          start_value: oldKr.start_value,
          target_value: oldKr.target_value,
          current_value: oldKr.start_value,
          unit: oldKr.unit || '',
          owner_emails: oldKr.owner_emails || [],
          mode: (oldKr.mode as KeyResult['mode']) || 'collaborative',
          owner_progress: {},
          owner_targets: oldKr.owner_targets ? { ...oldKr.owner_targets } : {},
          cycle: newCycle,
          status: 'on_track',
          progress_history: [],
          created_at: new Date().toISOString(),
        };
        await dispatch('addKeyResult', { teamId, goalId, keyResult: newKr });
        newKrs.push(newKr);
      }

      return { success: true, newKrs: newKrs };
    } catch (error) {
      console.error('Error rolling forward key results:', error);
      return { success: false, error: error };
    }
  },

  async invalidateKrSuggestionsCache(
    { rootState }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ): Promise<boolean> {
    try {
      const company = rootState.authen.user.company;

      const snapshot = await firebase.firestore()
        .collection('suggested_key_results')
        .where('company', '==', company)
        .where('teamId', '==', teamId)
        .where('goalId', '==', goalId)
        .where('status', '==', 'pending')
        .get();

      const batch = firebase.firestore().batch();
      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { status: 'invalidated' });
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error invalidating KR suggestions cache:', error);
      return false;
    }
  },

  async composeMetricRecipe(
    { commit, state }: any,
    { krId, prompt, krContext, toolkitHint }: { krId: string; prompt: string; krContext: any; toolkitHint?: string }
  ) {
    commit('setComposerComposing', true);
    try {
      const response = await metricSourceService.composeMetricRecipe({
        prompt,
        kr_context: krContext,
        toolkit_hint: toolkitHint,
      });
      commit('setComposerRecipe', response);
      return { success: true, recipe: response };
    } catch (error) {
      console.error('composeMetricRecipe failed', error);
      return { success: false, error };
    } finally {
      commit('setComposerComposing', false);
    }
  },

  async previewMetricRecipe(
    { commit }: any,
    { recipe, connectedAccountId, toolkitSlug, krContext, regenerateNote }: {
      recipe: metricSourceService.CompiledRecipe;
      connectedAccountId?: string;
      toolkitSlug?: string;
      krContext: any;
      regenerateNote?: boolean;
    }
  ) {
    commit('setComposerPreviewing', true);
    try {
      const response = await metricSourceService.previewMetricRecipe({
        recipe,
        connected_account_id: connectedAccountId,
        toolkit_slug: toolkitSlug,
        kr_context: krContext,
        regenerate_note: regenerateNote,
      });
      commit('setComposerPreview', response);
      return { success: true, preview: response };
    } catch (error) {
      console.error('previewMetricRecipe failed', error);
      return { success: false, error };
    } finally {
      commit('setComposerPreviewing', false);
    }
  },

  async saveKrMetricSource(
    { rootState }: any,
    { teamId, goalId, krId, metricSource }: {
      teamId: string;
      goalId: string;
      krId: string;
      metricSource: metricSourceService.MetricSourcePayload;
    }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      const response = await metricSourceService.saveKrMetricSource({
        company_id: companyId,
        goal_id: goalId,
        kr_id: krId,
        metric_source: metricSource,
      });
      return { success: true, ...response };
    } catch (error) {
      console.error('saveKrMetricSource failed', error);
      return { success: false, error };
    }
  },

  async runKrAutomationNow(
    { rootState }: any,
    { goalId, krId }: { teamId: string; goalId: string; krId: string }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      const response = await metricSourceService.runKrNow({
        company_id: companyId,
        goal_id: goalId,
        kr_id: krId,
      });
      return { success: true, ...response };
    } catch (error) {
      console.error('runKrAutomationNow failed', error);
      return { success: false, error };
    }
  },

  async listConnectedAccounts(
    { commit }: any,
    { toolkitSlug }: { toolkitSlug?: string }
  ) {
    try {
      const response = await metricSourceService.listConnectedAccounts({ toolkit_slug: toolkitSlug });
      if (toolkitSlug) {
        commit('setConnectedAccounts', { toolkitSlug, accounts: response.accounts });
      } else {
        // Bulk list: fan out per-slug commits so the global `hasActiveConnection`
        // getter is reliable for tile indicators across the app (composer
        // shortcuts, picker grid, etc.) even without a per-slug subscription.
        const grouped: Record<string, any[]> = {};
        for (const account of (response.accounts || [])) {
          const slug = account.toolkit_slug;
          if (!slug) continue;
          if (!grouped[slug]) grouped[slug] = [];
          grouped[slug].push(account);
        }
        for (const slug of Object.keys(grouped)) {
          commit('setConnectedAccounts', { toolkitSlug: slug, accounts: grouped[slug] });
        }
      }
      return { success: true, accounts: response.accounts };
    } catch (error) {
      console.error('listConnectedAccounts failed', error);
      return { success: false, error, accounts: [] };
    }
  },

  async startConnectAccount(
    _ctx: any,
    { toolkitSlug }: { toolkitSlug: string }
  ) {
    try {
      const response = await metricSourceService.startConnectAccount({ toolkit_slug: toolkitSlug });
      return { success: true, ...response };
    } catch (error) {
      console.error('startConnectAccount failed', error);
      return { success: false, error };
    }
  },

  async revokeConnectedAccount(
    _ctx: any,
    { accountId }: { accountId: string }
  ) {
    try {
      await metricSourceService.revokeConnectedAccount({ account_id: accountId });
      return { success: true };
    } catch (error) {
      console.error('revokeConnectedAccount failed', error);
      return { success: false, error };
    }
  },

  /**
   * Fetches and caches the FULL Composio toolkit catalog so the Browse modal
   * can search across all 250+ apps. Always fetches without a query — search
   * happens client-side against the cached catalog. Skips the network round
   * trip entirely if the catalog is already hot (loaded within the last hour
   * or currently loading).
   *
   * Pass `force: true` to bypass the freshness check.
   */
  async listToolkits(
    { commit, state }: any,
    { force }: { force?: boolean } = {}
  ) {
    const ONE_HOUR = 60 * 60 * 1000;
    const isFresh = state.toolkitCatalog.length > 0
      && Date.now() - state.toolkitCatalogLoadedAt < ONE_HOUR;
    if ((isFresh || state.toolkitCatalogLoading) && !force) {
      return { success: true, toolkits: state.toolkitCatalog };
    }

    commit('setToolkitCatalogLoading', true);
    try {
      const response = await metricSourceService.listToolkits();
      commit('setToolkitCatalog', response.toolkits);
      return { success: true, toolkits: response.toolkits };
    } catch (error) {
      console.error('listToolkits failed', error);
      return { success: false, error, toolkits: state.toolkitCatalog };
    } finally {
      commit('setToolkitCatalogLoading', false);
    }
  },

  subscribeConnectedAccounts(
    { commit, state, rootState, dispatch }: any,
    { toolkitSlug }: { toolkitSlug: string }
  ) {
    if (state.connectedAccountsUnsubscribers[toolkitSlug]) {
      return;
    }
    // In demo mode there is no Firestore-backed connected_accounts collection — the
    // canned backend hands out connections directly. We stub the unsubscriber and
    // let the explicit listConnectedAccounts call drive state updates.
    if (metricSourceService.isKrAutomationDemoMode()) {
      commit('setConnectedAccountsUnsubscriber', { toolkitSlug, unsub: () => null });
      dispatch('listConnectedAccounts', { toolkitSlug });
      return;
    }
    const companyId = rootState.authen.user.company;
    const unsub = firebase.firestore()
      .collection('connected_accounts')
      .where('company_id', '==', companyId)
      .where('toolkit_slug', '==', toolkitSlug)
      .onSnapshot((snapshot: any) => {
        const accounts = snapshot.docs
          .map((doc: any) => ({ id: doc.id, ...doc.data() }))
          .filter((a: any) => a.status !== 'revoked');
        commit('setConnectedAccounts', { toolkitSlug, accounts });
      }, (error: any) => {
        console.error('connected_accounts snapshot error', error);
      });
    commit('setConnectedAccountsUnsubscriber', { toolkitSlug, unsub });
  },

  unsubscribeConnectedAccounts(
    { commit, state }: any,
    { toolkitSlug }: { toolkitSlug: string }
  ) {
    const unsub = state.connectedAccountsUnsubscribers[toolkitSlug];
    if (unsub) {
      unsub();
      commit('setConnectedAccountsUnsubscriber', { toolkitSlug, unsub: null });
    }
  },

  resetMetricComposer({ commit }: any) {
    commit('resetComposer');
  },
};

const getters = {
  teams: (state: any, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['team/teamsall'] || [];
  },
  selectedTeam: (state: GoalsState) => state.selectedTeam,
  searchKeyword: (state: GoalsState) => state.searchKeyword,
  filteredTeams: (state: GoalsState, getters: any) => {
    let teams = getters.teams || [];

    // Filter by search keyword
    if (state.searchKeyword) {
      const keyword = state.searchKeyword.toLowerCase();
      teams = teams.filter((team: any) =>
        team.name && team.name.toLowerCase().includes(keyword)
      );
    }

    // Filter by selected team
    if (state.selectedTeam && state.selectedTeam !== 'All Teams') {
      // Show selected team and all its descendants
      const collectTeamAndDescendants = (teamId: string, allTeams: any[]): any[] => {
        const result: any[] = [];
        const team: any = allTeams.find((t: any) => t.key === teamId);
        if (team) result.push(team);

        // Find children
        const children = allTeams.filter((t: any) => t.parent === teamId);
        children.forEach((child: any) => {
          result.push(...collectTeamAndDescendants(child.key, allTeams));
        });

        return result;
      };

      teams = collectTeamAndDescendants(state.selectedTeam, getters.teams);
    }

    return teams;
  },
  editingTeamId: (state: GoalsState) => state.editingTeamId,
  editingTeam: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    if (!state.editingTeamId) return null;
    const teams = rootGetters['team/teamsall'] || [];
    return teams.find((t: any) => t.key === state.editingTeamId);
  },
  goalsTeamAI: (state: GoalsState) => state.goalsTeamAI,
  goalsLoaded: (state: GoalsState) => state.goalsLoaded,
  goalsLoading: (state: GoalsState) => state.goalsLoading,
  showArchivedGoals: (state: GoalsState) => state.showArchivedGoals,

  // Active goals (non-archived) by team
  activeGoalsTeamAI: (state: GoalsState) => {
    const result: Record<string, any[]> = {};
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      result[teamId] = state.goalsTeamAI[teamId].filter((goal: any) => !goal.archived);
    });
    return result;
  },

  activeGoalsCount: (state: GoalsState) => {
    return Object.keys(state.goalsTeamAI).reduce((count, teamId) => {
      const teamGoals = Array.isArray(state.goalsTeamAI[teamId]) ? state.goalsTeamAI[teamId] : [];
      return count + teamGoals.filter((goal: any) => !goal.archived).length;
    }, 0);
  },

  // Archived goals by team
  archivedGoalsTeamAI: (state: GoalsState) => {
    const result: Record<string, any[]> = {};
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      const archivedGoals = state.goalsTeamAI[teamId].filter((goal: any) => goal.archived);
      if (archivedGoals.length > 0) {
        result[teamId] = archivedGoals;
      }
    });
    return result;
  },

  // Count of archived goals across all teams
  archivedGoalsCount: (state: GoalsState) => {
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      count += state.goalsTeamAI[teamId].filter((goal: any) => goal.archived).length;
    });
    return count;
  },

  // Goal alignment getters - consume focus store data
  goalAlignmentStats: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/goalAlignmentStats'] || {};
  },

  alignedFocusCount: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/alignedFocusCount'] || 0;
  },

  unalignedFocusCount: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/unalignedFocusCount'] || 0;
  },

  // Enrich each goal with focus stats
  goalsWithFocusStats: (state: GoalsState, getters: any) => {
    const alignmentStats = getters.goalAlignmentStats;
    const goalsTeamAI = state.goalsTeamAI;
    const enrichedGoals: Record<string, any[]> = {};

    Object.keys(goalsTeamAI).forEach((teamId) => {
      enrichedGoals[teamId] = goalsTeamAI[teamId].map((goal: any) => {
        const stats = alignmentStats[goal.goal_id] || {
          peopleCount: 0,
          openCount: 0,
          doneCount: 0,
          totalItems: 0,
        };
        return {
          ...goal,
          focusStats: stats,
          hasFocus: stats.totalItems > 0,
          key_results: goal.key_results || [],
          progress: (() => {
            const krs = goal.key_results || [];
            if (krs.length === 0) return 0;
            const total = krs.reduce((sum: number, kr: any) => sum + calculateKrProgress(kr), 0);
            return Math.round(total / krs.length);
          })(),
        };
      });
    });

    return enrichedGoals;
  },

  // List of goals with no focus (gaps)
  goalsWithoutFocus: (state: GoalsState, getters: any) => {
    const goalsWithStats = getters.goalsWithFocusStats;
    const gaps: any[] = [];

    Object.keys(goalsWithStats).forEach((teamId) => {
      goalsWithStats[teamId].forEach((goal: any) => {
        if (!goal.hasFocus) {
          gaps.push({
            ...goal,
            teamId,
          });
        }
      });
    });

    return gaps;
  },

  // Total goals count across all teams
  totalGoalsCount: (state: GoalsState) => {
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      count += state.goalsTeamAI[teamId].length;
    });
    return count;
  },

  // Count of goals that have at least one focus item
  goalsWithFocusCount: (state: GoalsState, getters: any) => {
    const alignmentStats = getters.goalAlignmentStats;
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      state.goalsTeamAI[teamId].forEach((goal: any) => {
        if (alignmentStats[goal.goal_id] && alignmentStats[goal.goal_id].totalItems > 0) {
          count++;
        }
      });
    });
    return count;
  },

  /**
   * Get goals from ancestor teams for a given team (for linking modal)
   * Returns goals grouped by team with team metadata
   */
  ancestorGoalsForTeam: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (teamId: string) => {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Get ancestor teams by traversing parent chain
    const ancestors: any[] = [];
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    // For each ancestor team, get their active goals
    ancestors.forEach((team: any) => {
      const teamGoals = goalsTeamAI[team.key] || [];
      // Only include non-archived goals for linking
      const activeGoals = teamGoals.filter((g: any) => !g.archived);
      
      if (activeGoals.length > 0) {
        result.push({
          team: {
            key: team.key,
            name: team.name,
            color: team.color || '#747474',
          },
          goals: activeGoals.map((goal: any) => ({
            goal_id: goal.goal_id,
            quick_preview: goal.quick_preview,
            goal_description: goal.goal_description,
            teamId: team.key,
            teamName: team.name,
            teamColor: team.color || '#747474',
          })),
        });
      }
    });
    
    return result;
  },

  /**
   * Get full details for a list of goal IDs (for tooltip display)
   * Returns array of goal objects with team info
   */
  getParentGoalDetails: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (goalIds: string[]) => {
    if (!goalIds || goalIds.length === 0) return [];
    
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Create a map of all goals for fast lookup
    const goalMap: Record<string, any> = {};
    Object.keys(goalsTeamAI).forEach((teamId) => {
      const team = allTeams.find((t: any) => t.key === teamId);
      goalsTeamAI[teamId].forEach((goal: any) => {
        goalMap[goal.goal_id] = {
          ...goal,
          teamId,
          teamName: (team && team.name) ? team.name : 'Unknown Team',
          teamColor: (team && team.color) ? team.color : '#747474',
        };
      });
    });
    
    // Lookup each requested goal ID
    goalIds.forEach((goalId: string) => {
      const goal = goalMap[goalId];
      if (goal) {
        result.push(goal);
      }
    });
    
    return result;
  },

  /**
   * Flat list of all goals across all teams (for reverse lookups)
   */
  allGoalsFlat: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    Object.keys(goalsTeamAI).forEach((teamId) => {
      const team = allTeams.find((t: any) => t.key === teamId);
      goalsTeamAI[teamId].forEach((goal: any) => {
        result.push({
          ...goal,
          teamId,
          teamName: (team && team.name) ? team.name : 'Unknown Team',
          teamColor: (team && team.color) ? team.color : '#747474',
        });
      });
    });
    
    return result;
  },

  goalProgress: (state: GoalsState) => (teamId: string, goalId: string): number => {
    const teamGoals = state.goalsTeamAI[teamId] || [];
    const goal = teamGoals.find((g: any) => g.goal_id === goalId);
    if (!goal || !goal.key_results || goal.key_results.length === 0) return 0;
    const totalProgress = goal.key_results.reduce((sum: number, kr: any) => {
      return sum + calculateKrProgress(kr);
    }, 0);
    return Math.round(totalProgress / goal.key_results.length);
  },

  goalKeyResults: (state: GoalsState) => (teamId: string, goalId: string): any[] => {
    const teamGoals = state.goalsTeamAI[teamId] || [];
    const goal = teamGoals.find((g: any) => g.goal_id === goalId);
    return (goal && goal.key_results) || [];
  },

  suggestedKrLoading: (state: GoalsState) => state.suggestedKrLoading,
  suggestedKrResult: (state: GoalsState) => state.suggestedKrResult,
  suggestedKrGoalId: (state: GoalsState) => state.suggestedKrGoalId,

  resolveOwner: (state: GoalsState, getters: any, rootState: any) => (email: string) => {
    if (!email) return null;
    const allUsers = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {};
    const key = email.replace(/\./g, '||');
    const user = allUsers[key];
    if (!user) return null;
    return {
      email: user.email || email,
      fullname: ((user.firstname || '') + ' ' + (user.lastname || '')).trim(),
      large_pic: user.profilepic_crop || user.large_pic || '',
      house_color: user.house_color || '#ddd',
    };
  },

  teamMembersForGoal: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (teamId: string) => {
    const allUsers = (rootState.main && rootState.main.allUser) ? rootState.main.allUser : {};
    const allTeams = rootGetters['team/teamsall'] || [];

    // Collect this team + all descendant team IDs
    const teamIds = new Set<string>();
    const collectDescendants = (id: string) => {
      teamIds.add(id);
      for (const t of allTeams) {
        if (t.parent === id && !teamIds.has(t.key)) {
          collectDescendants(t.key);
        }
      }
    };
    collectDescendants(teamId);

    const members: any[] = [];
    for (const key of Object.keys(allUsers)) {
      const user = allUsers[key];
      if (user && teamIds.has(user.team)) {
        members.push({
          email: user.email,
          fullname: ((user.firstname || '') + ' ' + (user.lastname || '')).trim(),
          large_pic: user.profilepic_crop || user.large_pic || '',
          house_color: user.house_color || '#ddd',
          team_name: user.team_name || '',
        });
      }
    }
    return members.sort((a, b) => a.fullname.localeCompare(b.fullname));
  },

  selectableTeamsForGoal: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (teamId: string) => {
    const allTeams = rootGetters['team/teamsall'] || [];
    const teamIds = new Set<string>();
    const collectDescendants = (id: string) => {
      teamIds.add(id);
      for (const t of allTeams) {
        if (t.parent === id && !teamIds.has(t.key)) {
          collectDescendants(t.key);
        }
      }
    };
    collectDescendants(teamId);
    return allTeams
      .filter((t: any) => teamIds.has(t.key))
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
  },

  metricSourceForKr: (state: GoalsState) => (krId: string) => {
    for (const teamId of Object.keys(state.goalsTeamAI)) {
      const teamGoals = state.goalsTeamAI[teamId] || [];
      for (const goal of teamGoals) {
        for (const kr of (goal.key_results || [])) {
          if (kr.kr_id === krId) {
            return kr.metric_source || null;
          }
        }
      }
    }
    return null;
  },

  hasActiveConnection: (state: GoalsState) => (toolkitSlug: string) => {
    const accounts = state.connectedAccountsByToolkit[toolkitSlug] || [];
    return accounts.some((a: any) => a.status === 'active');
  },

  activeConnectionsFor: (state: GoalsState) => (toolkitSlug: string) => {
    const accounts = state.connectedAccountsByToolkit[toolkitSlug] || [];
    return accounts.filter((a: any) => a.status === 'active');
  },

  composerState: (state: GoalsState) => state.composer,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
